import {
  ArrowLeftRight,
  Building2,
  CalendarDays,
  CreditCard,
  KeyRound,
  LifeBuoy,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { isOwner, membershipAllows } from "../../auth/permissions";
import { PageHeader } from "../../ui/components";
import { BillingSettings } from "./BillingSettings";
import { CalendarFeedSettings } from "./CalendarFeedSettings";
import { OrganizationSettings } from "./OrganizationSettings";
import { OwnershipSettings } from "./OwnershipSettings";
import { PrivacySettings } from "./PrivacySettings";
import { SecuritySettings } from "./SecuritySettings";
import { SupportAccessSettings } from "./SupportAccessSettings";
import { TeamSettings } from "./TeamSettings";

// Everything this workspace connects to on the outside moved to its own Integrations area, so
// what is left here is the workspace itself: who it is, who works in it, and what it pays us.
type Section =
  | "organization"
  | "ownership"
  | "team"
  | "security"
  | "calendar-links"
  | "billing"
  | "privacy"
  | "support";

export function SettingsPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const membership = auth.session?.selectedMembership;
  const requested = location.pathname.split("/").filter(Boolean)[1] as Section | undefined;
  const sections: Array<{ id: Section; label: string; icon: typeof Building2; visible: boolean }> =
    [
      { id: "organization", label: "Organization", icon: Building2, visible: true },
      { id: "ownership", label: "Ownership", icon: ArrowLeftRight, visible: true },
      {
        id: "team",
        label: "Team",
        icon: UsersRound,
        visible: membershipAllows(membership, "users.manage"),
      },
      { id: "security", label: "Security", icon: KeyRound, visible: true },
      // A private calendar link belongs to whoever subscribes to it, so it stays behind the
      // calendar permission rather than moving with the administrator integrations.
      {
        id: "calendar-links",
        label: "Calendar links",
        icon: CalendarDays,
        visible: membershipAllows(membership, "calendar.view"),
      },
      {
        id: "billing",
        label: "Billing",
        icon: CreditCard,
        visible: membershipAllows(membership, "tenant.manage"),
      },
      {
        id: "privacy",
        label: "Privacy",
        icon: ShieldCheck,
        visible: membershipAllows(membership, "privacy.manage"),
      },
      { id: "support", label: "Support access", icon: LifeBuoy, visible: isOwner(membership) },
    ];
  const allowed = sections.filter((item) => item.visible);
  const section: Section =
    requested && allowed.some((item) => item.id === requested) ? requested : "organization";

  return (
    <>
      <PageHeader
        title="Settings"
        description="Manage your organization, team, subscription, privacy, and support options."
      />
      <nav className="inline settings-tabs" aria-label="Settings sections">
        {allowed.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className={`button ${section === item.id ? "primary" : "secondary"}`}
              type="button"
              aria-current={section === item.id ? "page" : undefined}
              onClick={() => navigate(`/settings/${item.id}`)}
            >
              <Icon size={17} /> {item.label}
            </button>
          );
        })}
      </nav>
      <div className="settings-content">
        {section === "organization" ? <OrganizationSettings /> : null}
        {section === "ownership" ? <OwnershipSettings /> : null}
        {section === "team" ? <TeamSettings /> : null}
        {section === "security" ? <SecuritySettings /> : null}
        {section === "calendar-links" ? <CalendarFeedSettings /> : null}
        {section === "billing" ? <BillingSettings /> : null}
        {section === "privacy" ? <PrivacySettings /> : null}
        {section === "support" ? <SupportAccessSettings /> : null}
      </div>
    </>
  );
}
